/**
 * Labelled RNG streams.
 *
 * Law: nothing in core/ ever calls Math.random. Every random draw in the game names
 * the stream it comes from (`rng.float('reels')`), because:
 *   - GLÜCK bends *declared* upcoming events on a *named* stream (design: economy.md),
 *   - streams are independent, so adding a draw in one machine cannot shift another
 *     machine's sequence — that is what makes replays survive content changes,
 *   - the per-stream draw counters go into the world hash, so a missed or extra draw
 *     is caught by the twin-run test instead of by a player.
 */

/** 32-bit FNV-1a over a string — stream label -> seed material. */
export function fnv1a32(s: string): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 0x01000193) >>> 0;
  }
  return h >>> 0;
}

/** splitmix32 — used only to expand a seed into stream state. */
function splitmix32(seed: number): () => number {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x9e3779b9) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return (t ^ (t >>> 14)) >>> 0;
  };
}

/** sfc32 — small, fast, good enough, and trivially serializable (4 × u32). */
export class Sfc32 {
  a: number;
  b: number;
  c: number;
  d: number;
  /** number of draws taken; part of the world hash. */
  draws = 0;

  constructor(seed: number) {
    const sm = splitmix32(seed);
    this.a = sm();
    this.b = sm();
    this.c = sm();
    this.d = sm();
    // warm up so nearby seeds decorrelate
    for (let i = 0; i < 12; i++) this.u32();
    this.draws = 0;
  }

  u32(): number {
    const t = (this.a + this.b) >>> 0;
    this.a = (this.b ^ (this.b >>> 9)) >>> 0;
    this.b = (this.c + (this.c << 3)) >>> 0;
    this.c = ((this.c << 21) | (this.c >>> 11)) >>> 0;
    this.d = (this.d + 1) >>> 0;
    const r = (t + this.d) >>> 0;
    this.c = (this.c + r) >>> 0;
    this.draws++;
    return r >>> 0;
  }

  float(): number {
    return this.u32() / 4294967296;
  }
}

export interface RngSnapshot {
  label: string;
  a: number;
  b: number;
  c: number;
  d: number;
  draws: number;
}

export class RngStreams {
  readonly seed: number;
  private readonly streams = new Map<string, Sfc32>();

  constructor(seed: number) {
    this.seed = seed >>> 0;
  }

  /** Get (lazily create) a named stream. Creation is order-independent by construction. */
  stream(label: string): Sfc32 {
    let s = this.streams.get(label);
    if (!s) {
      s = new Sfc32((this.seed ^ fnv1a32(label)) >>> 0);
      this.streams.set(label, s);
    }
    return s;
  }

  float(label: string): number {
    return this.stream(label).float();
  }

  /** Uniform integer in [0, n). */
  int(label: string, n: number): number {
    if (n <= 1) return 0;
    return Math.floor(this.float(label) * n) % n;
  }

  range(label: string, lo: number, hi: number): number {
    return lo + this.float(label) * (hi - lo);
  }

  pick<T>(label: string, arr: readonly T[]): T {
    if (arr.length === 0) throw new Error(`rng.pick on empty array (stream ${label})`);
    return arr[this.int(label, arr.length)]!;
  }

  /** Deterministic in-place shuffle (Fisher-Yates, descending). */
  shuffle<T>(label: string, arr: T[]): T[] {
    for (let i = arr.length - 1; i > 0; i--) {
      const j = this.int(label, i + 1);
      const t = arr[i]!;
      arr[i] = arr[j]!;
      arr[j] = t;
    }
    return arr;
  }

  /** Sorted by label so the hash never depends on stream creation order. */
  snapshot(): RngSnapshot[] {
    const labels = [...this.streams.keys()].sort();
    return labels.map((label) => {
      const s = this.streams.get(label)!;
      return { label, a: s.a, b: s.b, c: s.c, d: s.d, draws: s.draws };
    });
  }

  totalDraws(): number {
    let n = 0;
    for (const s of this.streams.values()) n += s.draws;
    return n;
  }
}
